import { FieldValue, FirebaseFirestore } from '@capacitor-firebase/firestore';
import { buildBrandFile } from '@hub/common';
import {
  fetchBrandLogoBlob,
  parseBrandDraft,
} from 'features/admin/brands/logic/brandChecks';
import { uploadBlobToStorage } from 'features/admin/protocol-editor/services/adminStorageUploadService';
import type { Timestamp } from '@capacitor-firebase/firestore';
import type { DbBrand } from '@hub/common';
import type { BrandDraft } from 'features/admin/brands/logic/brandChecks';

/** All brand records, newest first, with `createdAt` as a plain `{ seconds, nanoseconds }` object. */
export const loadBrands = async (): Promise<DbBrand[]> => {
  const { snapshots } = await FirebaseFirestore.getCollection<
    DbBrand<Timestamp>
  >({
    reference: 'brands',
  });
  return snapshots
    .flatMap(({ data }) =>
      data
        ? [
            {
              ...data,
              createdAt: {
                seconds: data.createdAt.seconds,
                nanoseconds: data.createdAt.nanoseconds,
              },
            },
          ]
        : []
    )
    .sort(
      (a, b) =>
        b.createdAt.seconds - a.createdAt.seconds || a.id.localeCompare(b.id)
    );
};

/** Under a new id, uploads the draft logo to `brands/{id}/logo.{ext}` (when
 *  set) and the brand file to `brands/{id}/brand.json`, then creates the
 *  record pointing at the file. A failed upload writes no record. */
export const publishBrand = async (
  draft: BrandDraft,
  user: { uid: string; email: string | null }
): Promise<DbBrand<FieldValue>> => {
  const id = crypto.randomUUID();
  let logoUrl: string | undefined;
  if (draft.logo) {
    const { blob, extension } = await fetchBrandLogoBlob(draft.logo);
    logoUrl = await uploadBlobToStorage(`brands/${id}/logo.${extension}`, blob);
  }
  const file = buildBrandFile({
    brandId: id,
    sponsorName: (draft.sponsorName ?? '').trim(),
    inputs: parseBrandDraft(draft).inputs,
    ...(logoUrl !== undefined && { logoUrl }),
  });
  const url = await uploadBlobToStorage(
    `brands/${id}/brand.json`,
    new Blob([JSON.stringify(file)], { type: 'application/json' })
  );
  const record: DbBrand<FieldValue> = {
    id,
    label: (draft.label ?? '').trim(),
    url,
    createdAt: FieldValue.serverTimestamp(),
    createdBy: user,
  };
  await FirebaseFirestore.setDocument({
    reference: `brands/${id}`,
    data: record,
  });
  return record;
};
